/** @jsx jsx */
import React from "react"
import { css, jsx } from "@emotion/core"
import { Link, useHistory } from "react-router-dom"
import format from "date-fns/format"
import {
  BaseButton,
  Button,
  Icon,
  IconButton,
  List,
  MultiButton,
  Placeholder,
  Spacer,
  Table,
} from "@cd2/components"
import { H3, H4, P } from "@cd2/text"
import { IRecord } from "../../types/schema"
import { formatDistanceToNowStrict, sub } from "date-fns"
import { IComponentProp, IComponentProps } from "@cd2/tools"
import { DropdownMenu } from "../DropdownMenu"
import { QuickToolTip } from "../QuickTooltip"
import { brandColors, colors } from "../EOSThemeProvider"
import { icons } from "../../constants/icons"
import { buildRoute, eRoutes } from "../../routes"
import { IconDefinition } from "@fortawesome/pro-solid-svg-icons"
import { ConditionalLink } from "../ConditionalLink"

export interface ISort {
  by: string | { path: string[] } | { count: string }
  direction: "ASC" | "DESC"
}

export function SageStatusColumn({
  sorted,
  onClick,
  getOrder = row => row as any,
  heading = "Sage No.",
}: {
  sorted?: "ASC" | "DESC"
  onClick?(): void
  heading?: string
  getOrder?: (
    row: Record<string, any>,
  ) => IRecord<"orders", "processed" | "sage_export_status" | "sage_order_number">
}) {
  return (
    <Table.Column heading={heading} sorted={sorted} onClick={onClick}>
      {row => {
        const order = getOrder(row)
        if (!order) {
          return <span>-</span>
        } else if (order.sage_order_number) {
          return <span>{order.sage_order_number}</span>
        } else if (order.processed) {
          return (
            <span>
              <span>Manually</span>
              <br />
              <span>processed</span>
            </span>
          )
        } else if (
          order.sage_export_status === "pending" ||
          order.sage_export_status === "in_progress"
        ) {
          return <span css={css("color: grey")}>Exporting...</span>
        } else if (order.sage_export_status === "failed") {
          return <span>Export Failed</span>
        } else {
          return <span>{order.sage_export_status}</span>
        }
      }}
    </Table.Column>
  )
}

export function CheckmarkColumn({
  checked,
  heading = "",
  onClick,
  sorted,
}: {
  checked: (row: Record<string, string>) => any
  heading?: string
  sorted?: "ASC" | "DESC"
  onClick?(): void
}) {
  return (
    <Table.Column heading={heading} css={css("width: 32px")} sorted={sorted} onClick={onClick}>
      {row =>
        checked(row) ? (
          <Icon
            icon={icons.check}
            css={css`
              width: 100%;
              text-align: center;
            `}
          />
        ) : null
      }
    </Table.Column>
  )
}

export function BackOrderLink({
  order,
}: {
  order: {
    childOrder: IRecord<"orders", "identifier"> | null
    parentOrder: IRecord<"orders", "identifier"> | null
  }
}) {
  return (
    <div>
      {order.childOrder ? (
        <Link
          to={buildRoute(eRoutes["/orders/:id/preview"], { id: order.childOrder.id })}
          css={css`
            display: block;
            margin: 2px -3px;
            white-space: nowrap;
          `}
        >
          <BaseButton
            buttonStyle="text"
            theme={{
              padding: { topAndBottom: 1, sides: 3 },
              fontSize: 11,
              color: brandColors.night,
              hoverColor: "rgba(0,0,0,0.05)",
            }}
            renderRight={() => <Icon icon={icons.external} size={8} />}
          >
            Back order of {order.childOrder.identifier}
          </BaseButton>
        </Link>
      ) : null}
      {order.parentOrder ? (
        <Link
          to={buildRoute(eRoutes["/orders/:id/preview"], { id: order.parentOrder.id })}
          css={css`
            display: block;
            margin: 2px -3px;
            white-space: nowrap;
          `}
        >
          <BaseButton
            buttonStyle="text"
            theme={{
              padding: { topAndBottom: 1, sides: 3 },
              fontSize: 11,
              color: brandColors.night,
              hoverColor: "rgba(0,0,0,0.05)",
            }}
            renderRight={() => <Icon icon={icons.external} size={8} />}
          >
            Has back order {order.parentOrder.identifier}
          </BaseButton>
        </Link>
      ) : null}
    </div>
  )
}

export function LoadingTable({ recordCount = 20, columnCount = 1, rowHeight = 30 }) {
  const data = Array(recordCount).fill({})
  const columns: React.ReactNode[] = Array(columnCount)

  for (let i = 0; i < columnCount; i++) {
    columns[i] = (
      <Table.Column key={i} heading={<Placeholder width="100%" height={15} />}>
        {() => <Placeholder width="100%" height={rowHeight} />}
      </Table.Column>
    )
  }

  return <Table data={data}>{columns}</Table>
}

interface IEditedAtColumnProps {
  field: { update: string; create: string; display?: string }
  sorted?: "ASC" | "DESC"
  heading?: string
  showTime?: boolean

  onClick?(): void
}

export function EditedAtColumn({
  field,
  heading = "Last Edit",
  sorted,
  onClick,
  showTime,
}: IEditedAtColumnProps) {
  const table = Table.useTable()

  if (table.isHeading) {
    if (heading) {
      return (
        <Table.Th onClick={onClick} sorted={sorted}>
          {heading}
        </Table.Th>
      )
    }
    return <th />
  }

  function displayValue(row: any) {
    const displayField = field.display || field.update
    const value = row[displayField]
    if (!value) {
      return "-"
    }
    const date = new Date(row[displayField])
    if (showTime) {
      return format(date, "dd/MM/yyyy - HH:mm")
    }
    return sub(new Date(), { days: 1 }) > date
      ? format(date, "dd/MM/yyyy - HH:mm")
      : formatDistanceToNowStrict(date, { addSuffix: true })
  }

  return (
    <Table.Column>
      {row => (
        <div
          css={css`
            display: inline-block;
          `}
        >
          <QuickToolTip
            content={
              <P size="small" color="white">
                Created At: {format(new Date(table.row[field.create]), "dd/MM/yyyy - HH:mm")}
                <br />
                Updated At: {format(new Date(table.row[field.update]), "dd/MM/yyyy - HH:mm")}
              </P>
            }
          >
            {displayValue(row)}
          </QuickToolTip>
        </div>
      )}
    </Table.Column>
  )
}

interface INotFoundProps {
  title?: string
  message?: string
  link?: { url: string; text: string }
}

export function NotFound({
  title = "Not found",
  message = "This record seems to have been moved or deleted.",
  link = { url: "/", text: "Back home" },
}: INotFoundProps) {
  const history = useHistory()
  return (
    <div
      css={css`
        text-align: center;
        padding: 200px 16px;
      `}
    >
      <H4 align="center" blockSpacing={0.5} weight={400}>
        {title}
      </H4>
      <H3 align="center">{message}</H3>

      <Spacer size={16} />
      <Button
        onClick={() => history.push(link.url)}
        buttonStyle="outline"
        leftIcon={icons.chevronLeft}
      >
        {link.text}
      </Button>
    </div>
  )
}

export function MenuColumn({
  items,
}: {
  items(row: any): IComponentProp<typeof DropdownMenu, "items"> | undefined
}) {
  const table = Table.useTable()

  if (table.isHeading) {
    return <th style={{ padding: 2 }} />
  } else {
    const menuItems = items(table.row)
    return (
      <td
        style={{ padding: 2, width: 1 }}
        css={css`
          position: relative;
        `}
      >
        {menuItems && (
          <DropdownMenu items={menuItems}>
            <IconButton
              icon={icons.ellipsis}
              padding={8}
              iconSize={14}
              buttonStyle="text"
              buttonColor="#999"
              square
            />
          </DropdownMenu>
        )}
      </td>
    )
  }
}

export function NoResultsBlock({
  title = "No Results",
  body = "There are no results to display",
  children,
}: {
  title?: string
  body?: string
  children?: React.ReactNode
}) {
  return (
    <div
      css={css`
        padding: 32px;
        background-color: #f5f5f5;
      `}
    >
      <H3 color="#666" align="center">
        {title}
      </H3>
      <P color="#bbb" align="center">
        {body}
      </P>
      {children && (
        <React.Fragment>
          <Spacer />
          {children}
        </React.Fragment>
      )}
    </div>
  )
}

export function NoResultsAdvanced({
  actions,
  multiButtonProps,
  rawCount,
  noDataMessage,
  noDataActions,
}: {
  rawCount?: number
  archivedCountFilter?: any
  actions: (IComponentProp<typeof MultiButton, "buttons">[number] & {
    display?: boolean
    message?: React.ReactNode
  })[]
  multiButtonProps?: Omit<IComponentProps<typeof MultiButton>, "buttons">
  noDataMessage?: string
  noDataActions?: IComponentProp<typeof MultiButton, "buttons">
}) {
  if (rawCount === 0) {
    return (
      <NoResultsBlock
        title="No results"
        body={noDataMessage ?? "There is no data to display for this resource"}
      >
        <MultiButton buttons={noDataActions ?? []} {...multiButtonProps} />
      </NoResultsBlock>
    )
  }
  const filteredActions = actions.filter(a => a.display)
  return (
    <NoResultsBlock
      title="No results for the current filters"
      body="Change your filters to view more records"
    >
      {filteredActions
        .filter(a => a.message)
        .map(a => (typeof a.message === "string" ? <P key={a.message}>{a.message}</P> : a.message))}
      {filteredActions.filter(a => a.message).length > 0 && <Spacer />}
      <MultiButton buttons={filteredActions} {...multiButtonProps} />
    </NoResultsBlock>
  )
}

export function IconColumn<T extends any>({
  icon,
  render,
  heading,
  iconSize,
}: {
  icon: (typeof icons)[keyof typeof icons]
  render?: (row: T, icon: React.ReactNode) => React.ReactNode
  heading?: string
  iconSize?: number
}) {
  return (
    <Table.Column
      heading={heading}
      css={css`
        padding: 8px;
        width: 1px;
        position: relative;
      `}
    >
      {row =>
        render ? (
          render(row, <Icon icon={icon} size={iconSize} />)
        ) : (
          <Icon icon={icon} size={iconSize} />
        )
      }
    </Table.Column>
  )
}

export function StatusIconsColumn({
  items,
}: {
  items(row: any): {
    icon: IconDefinition
    isActive: boolean
    link?: string
    positiveToolTip: string
    negativeToolTip: string
  }[]
}) {
  return (
    <Table.Column
      heading="Status"
      css={css`
        width: 1px;
      `}
    >
      {row => (
        <List size={2} horizontal>
          {items(row).map((item, i) => (
            <QuickToolTip
              key={i}
              content={item.isActive ? item.positiveToolTip : item.negativeToolTip}
            >
              <ConditionalLink to={item.link}>
                <div
                  css={css`
                    background-color: ${colors.primary};
                    width: 26px;
                    height: 26px;
                    text-align: center;
                    color: #000;
                    line-height: 26px;
                    border-radius: 5px;
                    opacity: ${item.isActive ? 1 : 0.2};
                  `}
                >
                  <Icon icon={item.icon} color="white" />
                </div>
              </ConditionalLink>
            </QuickToolTip>
          ))}
        </List>
      )}
    </Table.Column>
  )
}
